import { useCallback, useEffect, useState } from 'react';
import { useNotifier } from '@provider/notifier';
import { Modal, EyeIcon, Tooltip, CrossIcon, GyldendalLogoSmall } from '@ui/kit';
import { CSSRefTransition } from '@utils/react';
import { dialogCls, iframeWrapperCls, iframeCls, toolbarCls, buttonCls, throbberTransitionDurationMs, throbberCls } from './builder-lightbox.style.js';
import { makePreviewLightboxConfig } from './preview-lightbox.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useSyncCourseMutation } from '../api/mutation.sync.js';

const RETAIN_AFTER_CLOSE_MS = 5000;
const POLL_INTERVAL_MS = 1666;
function BuilderLightbox(props) {
  const notifier = useNotifier();
  const syncCourse = useSyncCourseMutation();
  const isLoading = useLoadingState(props.active);
  const url = `${process.env["COURSES_URL"]}/bygger/${props.course.id}`;
  const handleClose = useCallback(() => {
    props.onClose();
    syncCourse.mutate(props.course.id);
  }, [props, syncCourse]);
  const openPreview = useCallback(() => {
    notifier.modal(makePreviewLightboxConfig({
      course: props.course
    }));
  }, [notifier, props.course]);

  // Ensure that the course is synced once the lightbox is fully closed (which
  // happens after a delay to allow autosaving to complete, see parent
  // component)
  useEffect(() => {
    if (!props.active) {
      return;
    }
    const intervalId = setInterval(() => {
      syncCourse.mutate(props.course.id);
    }, POLL_INTERVAL_MS);
    return () => {
      setTimeout(() => clearInterval(intervalId), RETAIN_AFTER_CLOSE_MS);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.active]);
  return /*#__PURE__*/jsxs(Modal, {
    active: !!props.active,
    retainForMs: RETAIN_AFTER_CLOSE_MS,
    onClose: handleClose,
    className: dialogCls,
    noBackdrop: true,
    requiresInteraction: true,
    children: [/*#__PURE__*/jsxs("div", {
      className: iframeWrapperCls,
      children: [/*#__PURE__*/jsx("iframe", {
        className: iframeCls,
        src: url,
        title: `Udviddet bygger til forløbet ${props.course.title}`
      }), /*#__PURE__*/jsxs("div", {
        className: toolbarCls,
        children: [/*#__PURE__*/jsxs("button", {
          className: buttonCls,
          onClick: openPreview,
          children: [/*#__PURE__*/jsx(EyeIcon, {}), /*#__PURE__*/jsx("span", {
            children: "Se forl\xF8b"
          })]
        }), /*#__PURE__*/jsx(Tooltip, {
          placement: "top",
          body: "Minimer forl\xF8bsbygger",
          ariaType: "label",
          children: ref => /*#__PURE__*/jsx("button", {
            ref: ref,
            className: buttonCls,
            onClick: handleClose,
            children: /*#__PURE__*/jsx(CrossIcon, {})
          })
        })]
      })]
    }), /*#__PURE__*/jsx(CSSRefTransition, {
      in: isLoading,
      unmountOnExit: true,
      timeout: throbberTransitionDurationMs,
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        className: throbberCls,
        children: /*#__PURE__*/jsx(GyldendalLogoSmall, {
          height: 120,
          disableDefaultClassName: true
        })
      })
    })]
  });
}
function useLoadingState(active) {
  const [isLoading, setLoading] = useState(true);
  useEffect(() => {
    if (!active) {
      // bail out if there is no longer any courses to display
      return;
    }

    // register that we've begun a new viewer whenever course model changes, and
    // subscribe to the course-builder:ready event to recieve a callback to know
    // when it's ready for display
    setLoading(true);
    const handleMessage = evt => {
      if (evt.data === "course-builder:ready") {
        setLoading(false);
      }
    };

    // give up waiting after 6 seconds, so we don't end up showing a
    // throbber forever in case of errors
    const timeout = window.setTimeout(() => {
      setLoading(false);
    }, 6_000);
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      window.clearTimeout(timeout);
    };
  }, [active]);
  return isLoading;
}

export { BuilderLightbox };
//# sourceMappingURL=builder-lightbox.js.map
